
import { useDispatch, useSelector } from "react-redux";
import { createFailureAction, createSetDeviceVitalsInfo, createSetExpectedVersion, createSetSessionDetailsAction, createSetViewAction, createUpdateDeviceStateAction, createUpdateProgressAction } from "../state/reducer";
import { NOT_AVAILABLE, getDeviceId, getDeviceIdFromStorage, getDeviceType,getParsedUserAgent,isPageReloaded,storeDeviceId} from "../util";
import { client,SessionDetails,IpcMessageType,IADPState, ExtensionMessage, NetworkConnectionState, UpdateStates, eNAEPComponents, pingStages, FailureType, GreetingDetails, pingResults, LaunchErrorType } from "../typings";
import { v4 as uuidv4 } from "uuid";
import { useLaunchErrorService } from "./useLaunchErrorService";
declare global {
  interface Window {
    adpElectronBridge?: {
      greet: (arg0: client) => Promise<SessionDetails>;
      ping: () => Promise<boolean>;
      isUpdateAvailable: () => Promise<IpcMessageType>;
      postChromeAppState:(state:IADPState)=>void;
      enterKioskMode:()=>void;
      exitKioskMode:()=>void;
      listenOnNetworkStatusChanges:()=>void;
      sendSetupCode: (setupCode:string) => void;
      sendDeliveryMode: (deliveryMode:string) => void;
      getReleaseVersion: () => string;
    };
    adp: {
      extensionBridge: "";
      ipc: {
        publish: {
            ping:()=>Promise<boolean>
        };
        subscribe: {
          ping:(cb:(data:boolean)=> void)=>Promise<boolean>
        };
      };
    };
  }
}
export const isElectronBridgeAvailable = () => {
  return window["adpElectronBridge"];
};
export const postMessageToAdpBridge = (message: ExtensionMessage) => {
  window.postMessage(message);
};
export function useIPCBridge(
) {
  const dispatch = useDispatch();
  const state = useSelector((state:{rootState:IADPState})=>state.rootState)
  const {sendErrorLog} = useLaunchErrorService();
  const win: Window = window;
   
  /**
   * send greeting to ADP electron main process or ADP chrome extension.
   * If none is available, application is runnng in the browser without any backend support;
   * >>> Communication with Electron is established via preload.js which is part of the adp electron app and is injected
   * on the app startup. All methods are available at Window scope
   * >>> Communcatioon with Chrome Extension is established via Events that are emitted to the Window's context
   */
  async function sendGreeting() {
    //send greeting to Electron and obtain session and version details from the main process, if electron is not available read local manifest file
    const hardwareInfo=()=>{
      const promise =  new Promise<{hardwareInfo:any | string,systemInfo: any}>((resolve) => {
        const eventTimeoutId = setTimeout(() => {
          resolve({hardwareInfo:undefined,systemInfo: undefined});
        }, 5000);
          const handler =(event: any)=>{
            clearTimeout(eventTimeoutId);
            resolve(event.detail?.data);
              window.removeEventListener('HardwareInfoEvent',handler);
          }
            window.addEventListener('HardwareInfoEvent', handler);
        });
      
        return promise;
    }
    if (isElectronBridgeAvailable()) {
      console.log("[ADP Student App][IPCBridge] App is running in Electron, send greeting to main process");
      return win["adpElectronBridge"]?.greet({
        clientCode:state?.appState.appCode as string,
        clientVersion:state?.appState.appVersion as string,
        clientBuildDate: state?.appState.appBuildDate as string,
        clientURL:window.location.href
      }).then(async (details: any) => {
        dispatch(createSetDeviceVitalsInfo(details.deviceValidationResults))
        const storedDeviceId = await getDeviceIdFromStorage();
        const deviceId =  details?.deviceId && details?.deviceIdType !== "uuid" ? details.deviceId : storedDeviceId ?? getDeviceId();
        await storeDeviceId(deviceId);
        let sessionDetails: SessionDetails = {
          ...details,
          deviceId: deviceId,
          deviceIdType:details?.deviceIdType || "NotAvailable",
          deviceType:details?.deviceType,//device type will be set by electron, no need to modify
          networkConnectionState: window.navigator.onLine ? "Connected" : "N/A" as NetworkConnectionState,
          networkConnectionDetails: details?.networkConnectionState ,
          isPageReloaded: isPageReloaded(),
          extensionVersion: NOT_AVAILABLE,
          runtimePlatform: {
            platform: "Electron",
            version: details?.version,
            userAgent: navigator.userAgent
          },
        };
        dispatch(createSetSessionDetailsAction(sessionDetails));
       
        const nscEvent = new CustomEvent('network-state-changed-init', {
          detail: sessionDetails.networkConnectionState,
        });
        window.dispatchEvent(nscEvent);
        try {
          console.log("[ADP Student App][IPCBridge] send message to extenstion for hardware info")
          hardwareInfo().then((info)=>{
            console.log("hardwareInfo from extenstion",info)
          })
        }
        catch(e){
          console.warn("error getting hardwareInfo from extenstion",e);
        }
        //send greeting to the Chrome Extension loaded with Electron 
        console.log("[ADP Student App][IPCBridge] send greeting to ADP extension...");
        postMessageToAdpBridge({
          type: "Greeting",
          data: { client: "ADP", appLaunchId: sessionDetails.appLaunchId},
        });
        const handler = (event: any,) => {
          console.log(
            "[ADP Student App][IPCBridge] Chrome Extension Version:",
            event?.detail?.detail?.data?.chromeExtensionVersion
          );
           sessionDetails.extensionVersion = event?.detail?.detail?.data?.chromeExtensionVersion ?? NOT_AVAILABLE
           dispatch(createSetExpectedVersion({component: eNAEPComponents.CHROME_EXTENSION, version: sessionDetails.extensionVersion ?? NOT_AVAILABLE }));
           window.removeEventListener("GreetingEvent", handler);
      }
      return window.addEventListener("GreetingEvent", handler);
      });
    
    } else {
      console.log("[ADP Student App][IPCBridge] App is running in Browser, send greeting to chrome extension");
      //send greeting to chrome extension to check if Chrome extension is available
      const appLaunchId = uuidv4();
      //TODO - find a better way to check if extension is installed, e.g inject extension version
      const timeoutId = setTimeout(async() => {
        console.log("[ADP Student App][IPCBridge] extension is not available");
        console.warn('[ADP Student App][IPCBridge] app is running with limited functionalites, consider adding ADP Chrome extension for full experience')
        let storedDeviceId = await getDeviceIdFromStorage();
        if(!storedDeviceId){
          storedDeviceId = getDeviceId();
          await storeDeviceId(storedDeviceId);
       }
        let sessionDetails: SessionDetails = {
          appLaunchId: appLaunchId,
          deviceId: storedDeviceId, 
          deviceIdType: "NotAvailable",
          deviceModel:getParsedUserAgent().device ? `${getParsedUserAgent().device?.vendor ?? ""} ${getParsedUserAgent().device?.model ?? ""}` : NOT_AVAILABLE, 
          deviceType: getDeviceType(),
          networkConnectionState:window.navigator.onLine ? "Connected" : "N/A",
          isPageReloaded: isPageReloaded(),
          extensionVersion: NOT_AVAILABLE,
          runtimePlatform: {
            platform: "Browser",
            version: state.appState.appVersion,
            userAgent: navigator.userAgent
          },
        };
        dispatch(createSetSessionDetailsAction(sessionDetails));
        return;
      }, 5000);
      console.log("[ADP Student App][IPCBridge] send greeting to ADP extension...");
      postMessageToAdpBridge({
        type: "Greeting",
        data: { client: "ADP", appLaunchId: appLaunchId },
      });
      //listen for event from ChrEx
      const handler = async (event: any) => {
        console.log(
          "[ADP Student App][IPCBridge] Chrome Extension Version:",
          event?.detail?.detail?.data?.chromeExtensionVersion
        );
        clearTimeout(timeoutId);
        dispatch(createSetExpectedVersion({component: eNAEPComponents.CHROME_EXTENSION, version: event?.detail?.detail?.data?.chromeExtensionVersion ?? NOT_AVAILABLE }));
        let storedDeviceId = await getDeviceIdFromStorage();
        const uniqueID =  getDeviceType() === "NPE" ? event?.detail?.detail?.data?.deviceId : event?.detail?.detail?.data?.deviceSerialNumber ?? event?.detail?.detail?.data?.deviceId
        const deviceId = uniqueID && uniqueID?.indexOf("not available") === -1 && uniqueID!== "" ? getDeviceId(uniqueID) : storedDeviceId ?? getDeviceId();
        const network = window.navigator.onLine ? "Connected" : "N/A";
        const address = event?.detail?.detail?.data?.networkDetails?.ipv4event ??  NOT_AVAILABLE
        await storeDeviceId(deviceId);
        let sessionDetails: SessionDetails = {
          appLaunchId: appLaunchId,
          deviceId: deviceId, 
          deviceIdType: "NotAvailable",
          deviceModel:getParsedUserAgent().device?.model ? `${getParsedUserAgent().device.vendor ?? ""} ${getParsedUserAgent().device.model}` : NOT_AVAILABLE, 
          deviceType: getDeviceType(),
          networkConnectionState:network,
          networkConnectionDetails: {connectionType:network,address: address},
          isPageReloaded: isPageReloaded(),
          extensionVersion: event?.detail?.detail?.data?.chromeExtensionVersion,
          runtimePlatform: {
            platform: "ChromeWithExtension",
            version: state.appState.appVersion,
            userAgent: navigator.userAgent
          },
        };
         dispatch(createSetSessionDetailsAction(sessionDetails));
          window.removeEventListener("GreetingEvent", handler);
          const platformInfo=()=>{
            const promise =  new Promise<{os: string,arch: string,nacl_arch:string}>((resolve) => {
                const handler =(event: any)=>{
                  resolve(event.detail?.data);
                  window.removeEventListener('PlatformInfoEvent',handler);
                }
                  window.addEventListener('PlatformInfoEvent', handler);
              })
              return promise;
            };
          const  deviceDetails=()=>{
              const promise =  new Promise<string>((resolve) => {
                const eventTimeoutId = setTimeout(() => {
                  resolve("DeviceDetailsEvent did not resolve");
                }, 5000);
                  const handler =(event: any)=>{
                    clearTimeout(eventTimeoutId);
                    resolve(event.detail?.data);
                    window.removeEventListener('DeviceDetailsEvent',handler);
                  }
                    window.addEventListener('DeviceDetailsEvent', handler);
                })
                return promise;
            }
           const networkDetails=()=>{
              const promise =  new Promise<{macAddress: string, ipv4:string,ipv6:string} | string>((resolve) => {
                const eventTimeoutId = setTimeout(() => {
                  resolve("NetworkDetailsEvent did not resolve");
                }, 5000);
                  const handler =(event: any)=>{
                    clearTimeout(eventTimeoutId);
                    resolve(event.detail?.data);
                      window.removeEventListener('NetworkDetailsEvent',handler);
                  }
                    window.addEventListener('NetworkDetailsEvent', handler);
                });
              
                return promise;
            }
            const deviceSerialNumber=()=>{
              const promise =  new Promise<{macAddress: string, ipv4:string,ipv6:string} | string>((resolve) => {
                const eventTimeoutId = setTimeout(() => {
                  resolve("DeviceSerialNumber did not resolve");
                }, 5000);
                  const handler =(event: any)=>{
                    clearTimeout(eventTimeoutId);
                    resolve(event.detail?.data);
                      window.removeEventListener('DeviceSerialNumber',handler);
                  }
                    window.addEventListener('DeviceSerialNumber', handler);
                });
              
                return promise;
            }
            const deviceList=()=>{
              const promise =  new Promise<string>((resolve) => {
                const eventTimeoutId = setTimeout(() => {
                  resolve(NOT_AVAILABLE);
                }, 5000);
                  const handler =(event: any)=>{
                    clearTimeout(eventTimeoutId);
                    resolve(event.detail?.data);
                      window.removeEventListener('AudioDeviceEvent',handler);
                  }
                    window.addEventListener('AudioDeviceEvent', handler);
                });
              
                return promise;
            }
            Promise.all([hardwareInfo(),deviceList()]).then(async (values)=>{
              console.log(values);
             // const uniqueID = values[1] ??  values[4] ?? undefined
              //const deviceId = getDeviceId(uniqueID && uniqueID?.indexOf("not available") === -1 ? uniqueID : undefined);
             // await storeDeviceId(deviceId);
              //const address =  typeof values[2] === "string" ? values[2] : values[2]?.ipv4
              //const network = window.navigator.onLine ? "Connected" : "N/A";
               const deviceModel = values[0]?.hardwareInfo?.model ? `${values[0]?.hardwareInfo?.manufacturer ?? ""} ${values[0]?.hardwareInfo?.model}` : getParsedUserAgent().device?.model ? `${getParsedUserAgent().device.vendor ?? ""}${getParsedUserAgent().device.model}` : NOT_AVAILABLE;
               const systemInfo = values[0]?.systemInfo;
               let sessionDetails: SessionDetails = {
                deviceId: deviceId,
                deviceIdType: "NotAvailable",
                deviceModel:deviceModel,
                deviceType: getDeviceType(),
                appLaunchId: appLaunchId,
                isPageReloaded: isPageReloaded(),
                networkConnectionState:network,
                networkConnectionDetails: {connectionType:network,address: address},
                systemInfo: [...systemInfo,{type: "audioDevices", info: values[1]?.includes("not available") ?  NOT_AVAILABLE : values[1]}],
                extensionVersion: event?.detail?.detail.data?.chromeExtensionVersion ?? NOT_AVAILABLE,
                runtimePlatform: {
                  platform: "ChromeWithExtension",
                  version: event?.detail?.data?.chromeExtensionVersion,
                  userAgent: navigator.userAgent
                },
              }; 
              dispatch(createUpdateDeviceStateAction(sessionDetails))
              //dispatch(createSetSessionDetailsAction(sessionDetails));
            });
           
           //postMessageToAdpBridge({type:"NetworkDetails"}); 
           //postMessageToAdpBridge({type:"DeviceDetails"}); 
          // postMessageToAdpBridge({type:"PlatformInfo"}); 
           postMessageToAdpBridge({type:"HardwareInfo"});
           postMessageToAdpBridge({type:"AudioDevice"})  
           return;
          }
             
      return window.addEventListener("GreetingEvent", handler);
    }
  }
  async function getSoftwareStatusValues():Promise<GreetingDetails>{
    return new Promise<GreetingDetails>((resolve) => {
    if (isElectronBridgeAvailable()) {
      console.log("[ADP Student App][IPCBridge] App is running in Electron, send greeting to main process to get software status values.");
      (win["adpElectronBridge"] as any).greet({
        clientCode:state?.appState.appCode as string,
        clientVersion:state?.appState.appVersion as string,
        clientBuildDate: state?.appState.appBuildDate as string,
        clientURL:window.location.href
      }).then(async (details: any) => {
        const deviceId =  details?.deviceId  && details?.deviceIdType !== "uuid"? details.deviceId : getDeviceId();
        await storeDeviceId(deviceId);
        let sessionDetails: GreetingDetails = {
          appLaunchId: details.appLaunchId,
          deviceModel: details.deviceModel,
          deviceId: deviceId,
          deviceType:details?.deviceType,//device type will be set by electron, no need to modify
          runtimePlatform: {
            platform: "Electron",
            version: details?.version,
            userAgent: navigator.userAgent
          },
        };
        resolve(sessionDetails)
        
      });
    
    } else {
      
    postMessageToAdpBridge({
      type: "Greeting",
      data: { client: "ADP", appLaunchId: state.appState.appLaunchId },
    });
    //listen for event from ChrEx
    const handler = async (event: any) => {
      console.log(
        "[ADP Student App][IPCBridge] Chrome Extension Version:",
        event?.detail?.detail?.data?.chromeExtensionVersion
      );
      const uniqueID =  getDeviceType() === "NPE" ? event?.detail?.detail?.data?.deviceId : event?.detail?.detail?.data?.deviceSerialNumber ?? event?.detail?.detail?.data?.deviceId
      const deviceId = uniqueID && uniqueID?.indexOf("not available") === -1 && uniqueID!== "" ? getDeviceId(uniqueID) : getDeviceId();
      await storeDeviceId(deviceId);
      window.removeEventListener("GreetingEvent", handler);
          const hardwareInfo=()=>{
            const promise =  new Promise<{hardwareInfo:any | string,systemInfo: any}>((resolve) => {
              const eventTimeoutId = setTimeout(() => {
                resolve({hardwareInfo:undefined,systemInfo: undefined});
              }, 5000);
                const handler =(event: any)=>{
                  clearTimeout(eventTimeoutId);
                  resolve(event.detail?.data);
                    window.removeEventListener('HardwareInfoEvent',handler);
                }
                  window.addEventListener('HardwareInfoEvent', handler);
              });
            
              return promise;
          }
          Promise.all([hardwareInfo()]).then(async (values)=>{
             const deviceModel = values[0]?.hardwareInfo?.model ? `${values[0]?.hardwareInfo?.manufacturer ?? ""} ${values[0]?.hardwareInfo?.model}` : getParsedUserAgent().device?.model ? `${getParsedUserAgent().device.vendor ?? ""}${getParsedUserAgent().device.model}` : NOT_AVAILABLE;
             let sessionDetails: GreetingDetails = {
              deviceId: deviceId,
              deviceModel:deviceModel,
              deviceType: getDeviceType(),
              appLaunchId: state.appState.appLaunchId || uuidv4(),
              runtimePlatform: {
                platform: "ChromeWithExtension",
                version: event?.detail?.data?.chromeExtensionVersion,
                userAgent: navigator.userAgent
              },
            }; 
            resolve(sessionDetails)
          });
         return;
        }
           
      window.addEventListener("GreetingEvent", handler); 
      }
    });
  }
  async function sendPing(index:pingStages):Promise<pingResults>{
    return new Promise<pingResults>(async (resolve) =>{
    const ping= (cb:(response:boolean)=>void) => {
      const handler = () =>{
        window.removeEventListener("PingEvent",handler)
        cb(true)
      }
      window.addEventListener("PingEvent",handler)
      postMessageToAdpBridge({
        type: "Ping",
        data: {},
      });
    };
    const windowsPing = (cb:(response:any)=>void) =>{
      const regexString = /(?<=Electron\/)(.*?)(?=\.)/g
      const electronVersion = window.navigator.userAgent.match(regexString)?.[0]
      const electronCutOff = state.appState.configuration?.ping?.electronVersion|| "30"
      console.log("[Ping] Electron version: ",electronVersion)
      if(Number(electronVersion) > Number(electronCutOff)){
        console.log(`[Ping] Electron version is over ${electronCutOff}, using ping method`)
        window["adp"]?.ipc?.subscribe?.ping(cb);
        window["adp"]?.ipc?.publish?.ping();
      }
      else{
        console.log(`[Ping] Electron version is under ${electronCutOff}, skipping ping method`)
        cb("skipped")
      }
    }
  
    const attemptPing = () => {
      let pingAttempt = 0;
      const pingAttemptMethod = ()=>{
          const pingTimeout = setTimeout(() => {
            console.warn("we timed out of our ping call on attempt: ",pingAttempt);
            pingAttempt++;
            if(pingAttempt < (state.appState.configuration?.ping.retryAttempts ||  3)){
              pingAttemptMethod()
            }
            else{
              if(state.appState.configuration?.ping.failurePoints.includes(index)){
                sendErrorLog("PingError" as unknown as LaunchErrorType,{timeStamp: new Date(),errorMessage:`There was an error loading the extension.`});
                dispatch(state.appState.configuration?.ping.errorScreen.includes("WarningView") ? createSetViewAction("WarningView") : createFailureAction(state.appState.configuration?.ping.errorScreen ? state.appState.configuration?.ping.errorScreen as FailureType : "ApplicationError"));
                 resolve("failure")
              }
              else{
                 resolve("skipped")
              }
            }
          }, state.appState.configuration?.ping.retryTimeoutInMS || 5000);
          console.log("[Ping] attempting to ping, this is attempt: ",pingAttempt)
          const handler = (response:boolean)=>{
            clearTimeout(pingTimeout);
            console.log("[Ping] we got a response from the ping call on attempt: ",pingAttempt)
            console.log("[Ping] the response is: ",response)
             if(response)resolve("success")
          }
          const windowsHandler = (response:any)=>{
            clearTimeout(pingTimeout);
            console.log("[Ping] we got a response from the ping call on attempt: ",pingAttempt)
            console.log("[Ping] the response is: ",response)
             if(response){
              if(response === "skipped"){
                resolve("skipped")
              }
              resolve("success")
             }
          }
          isElectronBridgeAvailable() ? windowsPing(windowsHandler) : ping(handler)
        }
      return pingAttemptMethod()
    }
    console.log("[Ping] attempting to ping on the ping IPC channel")
    return attemptPing()
  });
  }
  async function getReleaseVersion(){
    if (isElectronBridgeAvailable()) {
      const releaseVersion = win["adpElectronBridge"]?.getReleaseVersion();
      return releaseVersion
    }
    else{
      return undefined
    }
  }
  function checkForUpdates() {
    const onUpdateEvents = (e:any) => {
      console.table(e.detail);
      if(e.detail?.type === "update-fast-forward"){
        dispatch(createSetViewAction("ModeSelectionsView"));
      }
      else {
        dispatch(createUpdateProgressAction(e.detail));
      }
    };
    window.addEventListener("update-progress",onUpdateEvents);
    win["adpElectronBridge"]
      ?.isUpdateAvailable()
      .then((data: IpcMessageType) => {
        console.log("[ADP Student App][IPCBridge]");
        console.table(data);
        const updateType:UpdateStates = data.type;
        window.removeEventListener("update-progress",onUpdateEvents);
        if (updateType === "update-not-available") {
          dispatch(createUpdateProgressAction(data));
          setTimeout(()=>{
            dispatch(createSetViewAction("ModeSelectionsView"));
         },5000);
        } else if (updateType === "update-error") {
          dispatch(createFailureAction("UpdateError"));
        }
        else if (updateType === "update-complete") {
          setTimeout(()=>{
            dispatch(createUpdateProgressAction(data));
         },1000);
        }
        else if(updateType === "update-fast-forward"){
          dispatch(createSetViewAction("ModeSelectionsView"));
        }
        else if(updateType === "update-is-in-progress"){
          dispatch(createSetViewAction("UpdaterView"));
        }
      })
      .catch((e) => {
        console.error("[ADP Student App][IPCBridge]", e);
        dispatch(createFailureAction("UpdateError"));
        window.removeEventListener("update-progress",onUpdateEvents);
      });
  }
  function saveADPChromeAppState(state:IADPState){
    if (isElectronBridgeAvailable()) {
      win["adpElectronBridge"]?.postChromeAppState(state)
    }
    else {
      postMessageToAdpBridge({
        type: "PostChromeAppState"
      });
      
    }
  };
  function sendSetupCode (setupCode:string){
    win["adpElectronBridge"]?.sendSetupCode(setupCode);
  }
  function sendDeliveryMode (deliveryMode:string){
    win["adpElectronBridge"]?.sendDeliveryMode(deliveryMode);
  }
  function setKioskMode(){
    try {
      console.log("[ADP Student App][IPCBridge] setKioskMode called");
      win["adpElectronBridge"]?.enterKioskMode();
    }
    catch(e){
      console.warn("setKioskMode",e)
    }
  }
  function exitKioskMode(){
    try {
      console.log("[ADP Student App][IPCBridge] exitKioskMode called");
      win["adpElectronBridge"]?.exitKioskMode();
    }
    catch(e){
      console.warn("exitKioskMode",e)
    }
   
  }
  return { setKioskMode,exitKioskMode,sendGreeting, checkForUpdates,saveADPChromeAppState,sendSetupCode,sendDeliveryMode,getReleaseVersion,sendPing,getSoftwareStatusValues};
};

